import React, { useEffect, useRef, useState } from "react";
import { IsFeatureReady } from "src/constants/features";
import {
    ComposerInput,
    ComposerRow,
    EmptyNotes,
    NoteBody,
    NoteActionButton,
    NoteActions,
    NoteCard,
    NoteDeleteActions,
    NoteDeleteButton,
    NoteDeletePopover,
    NoteDeleteText,
    NoteHeader,
    NoteMeta,
    NotesContent,
    NotesHeader,
    NotesHeaderRow,
    NotesHint,
    NotesList,
    NotesPanel,
    NotesTab,
    NotesTabs,
    NotesTitle,
    NotesCollapseButton,
    SendButton,
    SendIcon
} from "./CallNotesPanel.styled";

const CallNotesPanel = ({ notes = [], onAddNote, onCollapse, onDeleteNote, onEditNote, isSending = false }) => {
    const [value, setValue] = useState("");
    const [editingNote, setEditingNote] = useState(null);
    const [notePendingDeletion, setNotePendingDeletion] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const notesListRef = useRef(null);

    useEffect(() => {
        const notesList = notesListRef.current;
        if (!notesList) {
            return;
        }

        notesList.scrollTo({ top: notesList.scrollHeight, behavior: "smooth" });
    }, [notes.length]);

    const submit = async event => {
        event.preventDefault();

        const body = value.trim();

        if (!body) {
            return;
        }

        setValue("");
        const result = editingNote
            ? await onEditNote?.(editingNote, body)
            : await onAddNote?.(body);

        if (result === false) {
            setValue(currentValue => currentValue || body);
        } else {
            setEditingNote(null);
        }
    };

    const beginEditing = note => {
        setEditingNote(note);
        setValue(note.body);
    };

    const deleteNote = async () => {
        if (!notePendingDeletion || isDeleting) {
            return;
        }

        setIsDeleting(true);
        try {
            const result = await onDeleteNote?.(notePendingDeletion);
            if (result !== false) {
                setNotePendingDeletion(null);
            }
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <>
            <NotesPanel>
                <NotesHeader>
                    <NotesHeaderRow>
                        <NotesTitle>Call Notes</NotesTitle>
                        <NotesCollapseButton
                            type="button"
                            title="Hide call notes"
                            aria-label="Hide call notes"
                            onClick={onCollapse}
                        >
                            ›
                        </NotesCollapseButton>
                    </NotesHeaderRow>
                    <NotesHint>
                        Capture decisions, follow-ups and key points during the call.
                    </NotesHint>
                </NotesHeader>

                <NotesTabs role="tablist" aria-label="Call notes tools">
                    <NotesTab type="button" role="tab" aria-selected="true" $active>
                        Notes
                    </NotesTab>
                    {IsFeatureReady.callTextRecordings() && (
                        <>
                            <NotesTab type="button" role="tab" aria-selected="false" aria-disabled="true" disabled>
                                AI Transcript
                            </NotesTab>
                            <NotesTab type="button" role="tab" aria-selected="false" aria-disabled="true" disabled>
                                AI Summary
                            </NotesTab>
                        </>
                    )}
                </NotesTabs>
                <NotesContent>
                    <NotesList ref={notesListRef}>
                        {notes.length === 0 ? (
                            <EmptyNotes>
                                <strong>No notes yet</strong>
                                <span>
                                    Add notes during the call to keep track of decisions and follow-ups.
                                </span>
                            </EmptyNotes>
                        ) : (
                            notes.map(note => (
                                <NoteCard key={note.id} $isMine={note.isMine}>
                                    <NoteBody $isMine={note.isMine}>{note.body}</NoteBody>
                                    <NoteHeader $isMine={note.isMine}>
                                        <strong>{note.authorName || "You"}</strong>
                                        <NoteMeta>
                                            {new Date(note.createdAt).toLocaleTimeString([], {
                                                hour: "2-digit",
                                                minute: "2-digit"
                                            })}
                                        </NoteMeta>
                                        {note.isMine && onEditNote && onDeleteNote && (
                                            <NoteActions>
                                                <NoteActionButton type="button" onClick={() => beginEditing(note)}>Edit</NoteActionButton>
                                                <NoteActionButton
                                                    type="button"
                                                    onClick={() => setNotePendingDeletion(note)}
                                                >
                                                    Delete
                                                </NoteActionButton>
                                            </NoteActions>
                                        )}
                                    </NoteHeader>
                                    {notePendingDeletion?.id === note.id && (
                                        <NoteDeletePopover role="dialog" aria-label="Delete call note confirmation">
                                            <NoteDeleteText>Are you sure you want to delete this note?</NoteDeleteText>
                                            <NoteDeleteActions>
                                                <NoteDeleteButton
                                                    type="button"
                                                    disabled={isDeleting}
                                                    onClick={() => setNotePendingDeletion(null)}
                                                >
                                                    Cancel
                                                </NoteDeleteButton>
                                                <NoteDeleteButton
                                                    type="button"
                                                    $danger
                                                    disabled={isDeleting}
                                                    onClick={deleteNote}
                                                >
                                                    {isDeleting ? "Deleting..." : "Delete"}
                                                </NoteDeleteButton>
                                            </NoteDeleteActions>
                                        </NoteDeletePopover>
                                    )}
                                </NoteCard>
                            ))
                        )}
                    </NotesList>

                    <ComposerRow onSubmit={submit}>
                        <ComposerInput
                            type="text"
                            value={value}
                            onChange={event => setValue(event.target.value)}
                            placeholder={editingNote ? "Edit note..." : "Add a note..."}
                        />
                        <SendButton type="submit" disabled={!value.trim()} aria-busy={isSending}>
                            <SendIcon viewBox="0 0 24 24" aria-hidden="true">
                                <path
                                    d="M21 3L10 14"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M21 3L14 21L10 14L3 10L21 3Z"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </SendIcon>
                        </SendButton>
                    </ComposerRow>
                </NotesContent>
            </NotesPanel>
        </>
    );
};

export default React.memo(CallNotesPanel);
